import { z } from "zod";

// =============================================================================
// INBOX ACCOUNT SCHEMAS (email expense ingestion + personal-address sending)
// =============================================================================

export const inboxProviderSchema = z.enum(["gmail", "microsoft"]);
export type InboxProviderSchema = z.infer<typeof inboxProviderSchema>;

export const inboxAccountStatusSchema = z.enum(["active", "paused", "needs-reconnect", "quota-blocked"]);
export type InboxAccountStatusSchema = z.infer<typeof inboxAccountStatusSchema>;

export const inboxPermissionSchema = z.enum(["detect", "send"]);
export type InboxPermissionSchema = z.infer<typeof inboxPermissionSchema>;

export const inboxAccountSchema = z.object({
  id: z.string(),
  provider: inboxProviderSchema,
  emailAddress: z.string(),
  senderName: z.string().nullable().optional(),
  canDetectExpenses: z.boolean(),
  canSendEmail: z.boolean(),
  status: inboxAccountStatusSchema,
  connectedAt: z.coerce.date(),
  lastSyncedAt: z.coerce.date().nullable(),
  lastSyncError: z.string().nullable(),
  organizationId: z.string(),
  userId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type InboxAccountSchema = z.infer<typeof inboxAccountSchema>;

// Query for GET /api/inbox/gmail/auth — comma-separated permission selection.
// `redirect` is a whitelisted destination key, never a raw URL (no open redirect).
export const inboxAuthQuerySchema = z.object({
  permissions: z
    .string()
    .transform((value) => value.split(",").filter(Boolean))
    .pipe(z.array(inboxPermissionSchema).min(1)),
  redirect: z.enum(["settings", "onboarding", "dashboard", "ios", "android"]).optional(),
});
export type InboxAuthQuerySchema = z.infer<typeof inboxAuthQuerySchema>;

// Gmail OAuth callback (Google includes `error` when the user denies)
export const gmailOAuthCallbackSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
});
export type GmailOAuthCallbackSchema = z.infer<typeof gmailOAuthCallbackSchema>;

export const getInboxAccountSchema = z.object({
  id: z.string().uuid(),
});
export type GetInboxAccountSchema = z.infer<typeof getInboxAccountSchema>;

export const updateInboxAccountPermissionSchema = z.object({
  permission: inboxPermissionSchema,
  enabled: z.boolean(),
});
export type UpdateInboxAccountPermissionSchema = z.infer<typeof updateInboxAccountPermissionSchema>;

export const setInboxAccountPausedSchema = z.object({
  paused: z.boolean(),
});
export type SetInboxAccountPausedSchema = z.infer<typeof setInboxAccountPausedSchema>;
